import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useUserCheck } from '../../hooks/api/useUserCheck'
import { DashboardLayout } from '../../components/layout/DashboardLayout'
import { LoadingState } from '../../components/ui/LoadingState'
import { ErrorState } from '../../components/ui/ErrorState'
import { Server, CheckCircle, XCircle, AlertCircle, Save, TestTube2 } from 'lucide-react'
import { supabase } from '../../lib/supabase'

interface KaseyaConfig {
  id: string
  vsa_server_url: string
  api_username: string
  connection_status: 'pending' | 'connected' | 'failed'
  last_connection_test_at: string | null
  last_sync_at: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

type ConnectionStatus = 'idle' | 'testing' | 'success' | 'error'
type SaveStatus = 'idle' | 'saving' | 'success' | 'error'

export const KaseyaSettingsPage = () => {
  const { user } = useAuth()
  const { data: userRecord, isLoading: userLoading, error: userError } = useUserCheck(user?.id || '')

  const [config, setConfig] = useState<KaseyaConfig | null>(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Form state
  const [vsaServerUrl, setVsaServerUrl] = useState('')
  const [apiUsername, setApiUsername] = useState('')
  const [apiPassword, setApiPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Status states
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('idle')
  const [connectionMessage, setConnectionMessage] = useState<string>('')
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [saveMessage, setSaveMessage] = useState<string>('')

  // Load existing configuration
  useEffect(() => {
    loadConfig()
  }, [])

  const loadConfig = async () => {
    try {
      setIsLoadingConfig(true)
      setLoadError(null)

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      const response = await supabase.functions.invoke('kaseya-config', {
        method: 'GET',
      })

      if (response.error) {
        throw new Error(response.error.message)
      }

      const result = response.data

      if (result.success && result.data) {
        const configData = result.data as KaseyaConfig
        setConfig(configData)
        setVsaServerUrl(configData.vsa_server_url || '')
        setApiUsername(configData.api_username || '')
        // Don't load password for security
        setApiPassword('')
      } else {
        // No config exists yet
        setConfig(null)
      }
    } catch (err: any) {
      console.error('Failed to load config:', err)
      setLoadError(err.message || 'Failed to load configuration')
    } finally {
      setIsLoadingConfig(false)
    }
  }

  const handleTestConnection = async () => {
    if (!vsaServerUrl || !apiUsername || !apiPassword) {
      setConnectionStatus('error')
      setConnectionMessage('Please fill in all fields before testing')
      return
    }

    try {
      setConnectionStatus('testing')
      setConnectionMessage('Testing connection to Kaseya VSA...')

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      const response = await supabase.functions.invoke('kaseya-config/test', {
        method: 'POST',
        body: {
          vsa_server_url: vsaServerUrl,
          api_username: apiUsername,
          api_password: apiPassword,
        },
      })

      if (response.error) {
        throw new Error(response.error.message)
      }

      const result = response.data

      if (result.success) {
        setConnectionStatus('success')
        setConnectionMessage(`Connection successful! Endpoint: ${result.endpoint || 'Kaseya API'}`)
      } else {
        setConnectionStatus('error')
        setConnectionMessage(result.error || 'Connection failed')
      }
    } catch (err: any) {
      console.error('Connection test failed:', err)
      setConnectionStatus('error')
      setConnectionMessage(err.message || 'Connection test failed')
    }
  }

  const handleSaveConfiguration = async () => {
    if (!vsaServerUrl || !apiUsername || !apiPassword) {
      setSaveStatus('error')
      setSaveMessage('Please fill in all fields')
      return
    }

    try {
      setSaveStatus('saving')
      setSaveMessage('Saving configuration...')

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      const response = await supabase.functions.invoke('kaseya-config', {
        method: 'POST',
        body: {
          vsa_server_url: vsaServerUrl,
          api_username: apiUsername,
          api_password: apiPassword,
        },
      })

      if (response.error) {
        throw new Error(response.error.message)
      }

      const result = response.data

      if (result.success) {
        setSaveStatus('success')
        setSaveMessage(result.message || 'Configuration saved successfully!')

        // Reload config to get updated data
        await loadConfig()

        // Clear password field for security
        setApiPassword('')

        // Clear success message after 3 seconds
        setTimeout(() => {
          setSaveStatus('idle')
          setSaveMessage('')
        }, 3000)
      } else {
        setSaveStatus('error')
        setSaveMessage(result.error || 'Failed to save configuration')
      }
    } catch (err: any) {
      console.error('Save failed:', err)
      setSaveStatus('error')
      setSaveMessage(err.message || 'Failed to save configuration')
    }
  }

  if (userLoading || isLoadingConfig) {
    return <LoadingState message="Loading Kaseya settings..." />
  }

  if (userError || !userRecord) {
    return <ErrorState title="User not found!" description="Please try signing in again." />
  }

  if (userRecord.role !== 'admin') {
    return <ErrorState title="Access Denied" description="Only administrators can access Kaseya settings." />
  }

  return (
    <DashboardLayout
      userRole={userRecord.role}
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100">
            <Server className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Kaseya VSA Integration</h1>
            <p className="mt-1 text-sm text-gray-500">
              Configure global Kaseya VSA credentials for device management
            </p>
          </div>
        </div>

        {/* Load Error */}
        {loadError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Failed to load configuration</h3>
              <p className="mt-1 text-sm text-red-700">{loadError}</p>
            </div>
          </div>
        )}

        {/* Current Status Card */}
        {config && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Current Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-500">Connection Status</div>
                <div className="mt-1 flex items-center gap-2">
                  {config.connection_status === 'connected' && (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-sm text-green-700">Connected</span>
                    </>
                  )}
                  {config.connection_status === 'failed' && (
                    <>
                      <XCircle className="h-5 w-5 text-red-600" />
                      <span className="text-sm text-red-700">Failed</span>
                    </>
                  )}
                  {config.connection_status === 'pending' && (
                    <>
                      <AlertCircle className="h-5 w-5 text-yellow-600" />
                      <span className="text-sm text-yellow-700">Pending</span>
                    </>
                  )}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Last Tested</div>
                <div className="mt-1 text-sm text-gray-900">
                  {config.last_connection_test_at
                    ? new Date(config.last_connection_test_at).toLocaleString()
                    : 'Never'}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Last Sync</div>
                <div className="mt-1 text-sm text-gray-900">
                  {config.last_sync_at
                    ? new Date(config.last_sync_at).toLocaleString()
                    : 'Never'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Configuration Form */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {config ? 'Update Configuration' : 'Configure Kaseya VSA'}
          </h2>

          <div className="space-y-4">
            {/* VSA Server URL */}
            <div>
              <label htmlFor="vsa-url" className="block text-sm font-medium text-gray-700">
                VSA Server URL <span className="text-red-600">*</span>
              </label>
              <input
                type="url"
                id="vsa-url"
                value={vsaServerUrl}
                onChange={(e) => setVsaServerUrl(e.target.value)}
                placeholder="https://corporatetech.vsax.net"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Your Kaseya VSA server URL (e.g., https://yourcompany.vsax.net)
              </p>
            </div>

            {/* Token ID (was API Username) */}
            <div>
              <label htmlFor="api-username" className="block text-sm font-medium text-gray-700">
                Token ID <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="api-username"
                value={apiUsername}
                onChange={(e) => setApiUsername(e.target.value)}
                placeholder="5691f36d1f76467b9967b4c0f3038deb"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                32-character hexadecimal Token ID from Kaseya VSA API token generation
              </p>
            </div>

            {/* Token Secret (was API Password) */}
            <div>
              <label htmlFor="api-password" className="block text-sm font-medium text-gray-700">
                Token Secret <span className="text-red-600">*</span>
              </label>
              <div className="relative mt-1">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="api-password"
                  value={apiPassword}
                  onChange={(e) => setApiPassword(e.target.value)}
                  placeholder={config ? 'Enter new token secret to update' : '1e653f23ae5b4d5ab4b5a454b955fea0...'}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-24 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-600 hover:text-gray-900"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                64-character hexadecimal Token Secret from Kaseya VSA API token generation
              </p>
            </div>
          </div>

          {/* Test Connection Button */}
          <div className="mt-6 border-t border-gray-200 pt-6">
            <button
              onClick={handleTestConnection}
              disabled={connectionStatus === 'testing'}
              className="inline-flex items-center gap-2 rounded-md bg-white border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <TestTube2 className={`h-4 w-4 ${connectionStatus === 'testing' ? 'animate-pulse' : ''}`} />
              {connectionStatus === 'testing' ? 'Testing...' : 'Test Connection'}
            </button>

            {/* Connection Status Message */}
            {connectionStatus !== 'idle' && (
              <div className={`mt-4 p-4 rounded-md flex items-start gap-3 ${
                connectionStatus === 'success' ? 'bg-green-50 border border-green-200' :
                connectionStatus === 'error' ? 'bg-red-50 border border-red-200' :
                'bg-blue-50 border border-blue-200'
              }`}>
                {connectionStatus === 'success' && <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />}
                {connectionStatus === 'error' && <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />}
                {connectionStatus === 'testing' && <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5 animate-pulse" />}
                <p className={`text-sm ${
                  connectionStatus === 'success' ? 'text-green-700' :
                  connectionStatus === 'error' ? 'text-red-700' :
                  'text-blue-700'
                }`}>
                  {connectionMessage}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Save Configuration Button */}
        <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div>
            <p className="text-sm font-medium text-gray-900">Save Configuration</p>
            <p className="text-xs text-gray-500 mt-1">
              {config ? 'Update global Kaseya credentials for all organizations' : 'Configure Kaseya integration for the first time'}
            </p>
          </div>
          <button
            onClick={handleSaveConfiguration}
            disabled={saveStatus === 'saving'}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className={`h-4 w-4 ${saveStatus === 'saving' ? 'animate-pulse' : ''}`} />
            {saveStatus === 'saving' ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>

        {/* Save Status Message */}
        {saveStatus !== 'idle' && (
          <div className={`p-4 rounded-md flex items-start gap-3 ${
            saveStatus === 'success' ? 'bg-green-50 border border-green-200' :
            saveStatus === 'error' ? 'bg-red-50 border border-red-200' :
            'bg-blue-50 border border-blue-200'
          }`}>
            {saveStatus === 'success' && <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />}
            {saveStatus === 'error' && <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />}
            {saveStatus === 'saving' && <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5 animate-pulse" />}
            <p className={`text-sm ${
              saveStatus === 'success' ? 'text-green-700' :
              saveStatus === 'error' ? 'text-red-700' :
              'text-blue-700'
            }`}>
              {saveMessage}
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
